export interface SprayNozzle {
  name: string;
  pressure: number;
  flow: number;
  color: string;
}

/**
 * Reference pressure (bar) / flow (L/min) pairs and swatch colors for the
 * standard ISO spray nozzle sizes, used to derive each nozzle's flow
 * coefficient in the PWM speed calculation.
 *
 * @remarks
 * Nominal ISO 10625 flows at 3 bar follow 0.4 L/min × the size code (01 → 0.4,
 * 03 → 1.2, 0067 → 0.27). The constants spec enforces this so a mistyped flow
 * fails the build instead of skewing the recommended speeds.
 */
export const NOZZLE_TABLE: SprayNozzle[] = [
  { name: 'ISO-0050', pressure: 3.0, flow: 0.2, color: 'rgb(113, 106, 154)' },
  { name: 'ISO-0067', pressure: 3.0, flow: 0.27, color: 'rgb(72, 78, 60)' },
  { name: 'ISO-0075', pressure: 3.0, flow: 0.3, color: 'rgb(210, 152, 158)' },
  { name: 'ISO-01', pressure: 3.0, flow: 0.4, color: 'rgb(229, 95, 37)' },
  { name: 'ISO-015', pressure: 3.0, flow: 0.6, color: 'rgb(11, 136, 90)' },
  { name: 'ISO-02', pressure: 3.0, flow: 0.8, color: 'rgb(247, 198, 65)' },
  { name: 'ISO-025', pressure: 3.0, flow: 1.0, color: 'rgb(128, 82, 134)' },
  { name: 'ISO-03', pressure: 3.0, flow: 1.2, color: 'rgb(0, 86, 128)' },
  { name: 'ISO-035', pressure: 3.0, flow: 1.4, color: 'rgb(100, 21, 36)' },
  { name: 'ISO-04', pressure: 3.0, flow: 1.6, color: 'rgb(209, 31, 39)' },
  { name: 'ISO-05', pressure: 3.0, flow: 2.0, color: 'rgb(82, 51, 36)' },
  { name: 'ISO-06', pressure: 3.0, flow: 2.4, color: 'rgb(145, 148, 149)' },
  { name: 'ISO-08', pressure: 3.0, flow: 3.2, color: 'rgb(235, 234, 226)' },
  { name: 'ISO-10', pressure: 3.0, flow: 4.0, color: 'rgb(9, 138, 179)' },
  { name: 'ISO-12', pressure: 3.0, flow: 4.8, color: 'rgb(168, 44, 69)' },
  { name: 'ISO-14', pressure: 3.0, flow: 5.6, color: 'rgb(149, 136, 101)' },
  { name: 'ISO-15', pressure: 3.0, flow: 6.0, color: 'rgb(96, 155, 71)' },
  { name: 'ISO-16', pressure: 3.0, flow: 6.4, color: 'rgb(159, 90, 48)' },
  { name: 'ISO-18', pressure: 3.0, flow: 7.2, color: 'rgb(13, 142, 141)' },
  { name: 'ISO-20', pressure: 3.0, flow: 8.0, color: 'rgb(30, 36, 41)' },
  { name: 'ISO-25', pressure: 3.0, flow: 10.0, color: 'rgb(80, 89, 91)' },
  { name: 'ISO-30', pressure: 3.0, flow: 12.0, color: 'rgb(199, 168, 128)' },
  { name: 'ISO-40', pressure: 3.0, flow: 16.0, color: 'rgb(235, 234, 226)' },
  { name: 'ISO-50', pressure: 3.0, flow: 20.0, color: 'rgb(128, 82, 134)' },
  { name: 'ISO-60', pressure: 3.0, flow: 24.0, color: 'rgb(247, 198, 65)' },
  { name: 'ISO-80', pressure: 3.0, flow: 32.0, color: 'rgb(229, 94, 37)' },
];

/** Index of the nozzle pre-selected when the form loads (ISO-03). */
export const DEFAULT_NOZZLE_INDEX = 7;

/** Converts L/min per nozzle and km/h into an application rate in L/ha. */
export const LHA_CONVERSION_FACTOR = 600;

/** Lowest duty cycle a PWM controller can sustain while still spraying evenly. */
export const MIN_DUTY_CYCLE = 0.3;

/** Duty cycle at which a PWM nozzle sprays at its full rated flow. */
export const MAX_DUTY_CYCLE = 1.0;

export const CM_TO_M_FACTOR = 100;

export const DEFAULT_TARGET_RATE = 100;
export const DEFAULT_SPACING_CM = 50;
export const DEFAULT_PRESSURE_MIN = 2.0;
export const DEFAULT_PRESSURE_MAX = 6.0;

/** Decimals shown for the calculated speeds. */
export const SPEED_DIGITS = 2;

/** Decimals shown for the nozzle flow in the nozzle selector. */
export const FLOW_DIGITS = 2;
